import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Link
} from '@mui/material';
import {
  Inventory2,
  Warning,
  Schedule,
  History,
  AssignmentTurnedIn,
  TaskAlt
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import {
  isStoreIntentCompleted,
  needsStoreIntentAction
} from '../../utils/workOrderStoreReview';
import { STORE_TAB } from '../Store';

const StoreDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockCount: 0,
    toolCalibrationDueCount: 0,
    recentActivities: []
  });
  const [lowStockItems, setLowStockItems] = useState([]);
  const [expiringItems, setExpiringItems] = useState([]);
  const [toolCalibrationDueItems, setToolCalibrationDueItems] = useState([]);
  const [woStorePending, setWoStorePending] = useState(0);
  const [woStoreClosed, setWoStoreClosed] = useState(0);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        inventoryRes,
        lowStockRes,
        expiringRes,
        toolCalRes,
        toolCalSummaryRes,
        issuancesRes,
        maintenanceRes
      ] = await Promise.allSettled([
        axios.get('/api/store/inventory'),
        axios.get('/api/store/inventory/low-stock'),
        axios.get('/api/store/inventory/expiring'),
        axios.get('/api/store/tools/calibration-due'),
        axios.get('/api/store/tools/calibration-summary'),
        axios.get('/api/store/issuances?status=ISSUED'),
        axios.get('/api/maintenance')
      ]);

      const inventory = inventoryRes.status === 'fulfilled' ? inventoryRes.value.data : [];
      const lowStock = lowStockRes.status === 'fulfilled' ? lowStockRes.value.data : [];
      const expiring = expiringRes.status === 'fulfilled' ? expiringRes.value.data : [];
      const toolCalItems = toolCalRes.status === 'fulfilled' ? toolCalRes.value.data : [];
      const toolCalSummary = toolCalSummaryRes.status === 'fulfilled'
        ? toolCalSummaryRes.value.data
        : { dueSoon: 0, overdue: 0 };
      const issuances = issuancesRes.status === 'fulfilled' ? issuancesRes.value.data : [];
      const maintenanceAll =
        maintenanceRes.status === 'fulfilled' && Array.isArray(maintenanceRes.value.data)
          ? maintenanceRes.value.data
          : [];

      const pending = maintenanceAll.filter(
        (m) =>
          m.status === 'SCHEDULED' &&
          m.planningApproved &&
          needsStoreIntentAction(m)
      ).length;
      const closed = maintenanceAll.filter(
        (m) =>
          m.needsStoreAction &&
          isStoreIntentCompleted(m) &&
          m.planningApproved &&
          m.status !== 'CANCELLED'
      ).length;
      setWoStorePending(pending);
      setWoStoreClosed(closed);

      const failedCalls = [
        inventoryRes,
        lowStockRes,
        expiringRes,
        toolCalRes,
        toolCalSummaryRes,
        issuancesRes
      ].filter((res) => res.status === 'rejected');
      if (failedCalls.length) {
        console.warn('Dashboard partial failures:', failedCalls);
        showToast('Some dashboard metrics are unavailable right now.', 'warning');
      }
      if (
        maintenanceRes.status === 'rejected' &&
        maintenanceRes.reason?.response?.status !== 403
      ) {
        console.warn('Maintenance list for store counts failed:', maintenanceRes.reason);
        showToast('Work order counts for store could not be loaded.', 'warning');
      }

      setStats({
        totalItems: inventory.length,
        lowStockCount: lowStock.length,
        toolCalibrationDueCount: (toolCalSummary.dueSoon || 0) + (toolCalSummary.overdue || 0),
        recentActivities: issuances.slice(0, 5)
      });

      setLowStockItems(lowStock.slice(0, 10));
      setExpiringItems(expiring.slice(0, 10));
      setToolCalibrationDueItems(toolCalItems.slice(0, 10));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showToast('Error loading dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return <Typography>Loading dashboard...</Typography>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Inventory2 sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Total Items</Typography>
              </Box>
              <Typography variant="h4">{stats.totalItems}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Warning sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Low Stock</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {stats.lowStockCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Schedule sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Tool calibration due</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {stats.toolCalibrationDueCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <History sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">Recent Activities</Typography>
              </Box>
              <Typography variant="h4">{stats.recentActivities.length}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssignmentTurnedIn sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">WO awaiting store action</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {woStorePending}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Scheduled, planning approved, needs store, not yet accepted.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TaskAlt sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">WO closed by store</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {woStoreClosed}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Needed store action and store has accepted; excludes cancelled work orders.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Open the <strong>Work orders requests</strong> tab for the list, purchase orders, and accept or reject.
          </Typography>
        </Grid>

        {/* Low Stock Alerts */}
        {lowStockItems.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Low Stock Alerts
              </Typography>
              <List dense>
                {lowStockItems.map((item) => (
                  <ListItem key={item._id}>
                    <ListItemText
                      primary={item.partNumber}
                      secondary={`Qty: ${item.quantity} / Reorder: ${item.reorderPoint || 'N/A'}`}
                    />
                    <Chip label="Low Stock" color="warning" size="small" />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Expiring Items */}
        {expiringItems.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Expiring Soon (30 days)
              </Typography>
              <List dense>
                {expiringItems.map((item) => (
                  <ListItem key={item._id}>
                    <ListItemText
                      primary={item.partNumber}
                      secondary={`Expires: ${new Date(item.expiryDate).toLocaleDateString()}`}
                    />
                    <Chip
                      label={new Date(item.expiryDate) < new Date() ? 'Expired' : 'Expiring'}
                      color={new Date(item.expiryDate) < new Date() ? 'error' : 'warning'}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Tool Control: calibration due within 30 days or overdue */}
        {toolCalibrationDueItems.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tool calibration due
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Tools registered in Tool Control that are due within 30 days or overdue.
              </Typography>
              <List dense>
                {toolCalibrationDueItems.map((row) => {
                  const due = row.calibrationDueDate ? new Date(row.calibrationDueDate) : null;
                  const overdue = row.calibrationStatus === 'OVERDUE' || (due && due < new Date());
                  return (
                    <ListItem key={row._id}>
                      <ListItemText
                        primary={row.toolNumber}
                        secondary={`${row.description || ''}${due ? ` · Due ${due.toLocaleDateString()}` : ''}`}
                      />
                      <Chip
                        label={overdue ? 'Overdue' : 'Due ≤30d'}
                        color={overdue ? 'error' : 'warning'}
                        size="small"
                      />
                    </ListItem>
                  );
                })}
              </List>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/store', { state: { tab: STORE_TAB.TOOL_CONTROL } })}
                sx={{ mt: 1 }}
              >
                View in Tool Control
              </Link>
            </Paper>
          </Grid>
        )}

        {/* Recent Issuances */}
        {stats.recentActivities.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recent Issuances
              </Typography>
              <List dense>
                {stats.recentActivities.map((issuance) => (
                  <ListItem key={issuance._id}>
                    <ListItemText
                      primary={`${issuance.items.length} item(s) issued`}
                      secondary={new Date(issuance.issuedDate).toLocaleString()}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* No Alerts Message */}
        {lowStockItems.length === 0 && expiringItems.length === 0 && toolCalibrationDueItems.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="success">
              No alerts at this time. All inventory levels are healthy.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default StoreDashboard;
